import { useEffect, useRef, useState } from "react";
import { apiPost } from "../../api/client";
import { useTranslation } from "../../i18n";

const PRESETS = [1, 5, 10];

interface TimerProps {
  publicId: string;
  timerEndsAt: string | null;
  /** Only the owner of an ACTIVE retro may start or stop the timer. */
  canControl: boolean;
  onChanged: () => void;
  /** Fired once, when a running countdown reaches zero on this screen. */
  onFinished?: () => void;
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function Timer({
  publicId,
  timerEndsAt,
  canControl,
  onChanged,
  onFinished,
}: TimerProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);

  const endsAt = timerEndsAt ? new Date(timerEndsAt).getTime() : null;
  const remaining = endsAt === null ? null : endsAt - now;
  const isRunning = remaining !== null && remaining > 0;
  const isFinished = remaining !== null && remaining <= 0;

  // Tick only while a countdown is actually on screen.
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  /**
   * Fire only on the transition from running to finished, never on mount.
   * Opening a board whose timer expired an hour ago should be quiet — the
   * moment has passed, and every late arrival flashing would be noise.
   */
  const sawRunning = useRef(false);
  useEffect(() => {
    if (isRunning) {
      sawRunning.current = true;
      return;
    }

    if (isFinished && sawRunning.current) {
      sawRunning.current = false;
      onFinished?.();
    }
  }, [isRunning, isFinished, onFinished]);

  /**
   * Adds to whatever is left on the clock rather than replacing it, so a
   * facilitator can build up 2 minutes with two clicks of "+1 min". The server
   * does the arithmetic — it owns the deadline.
   */
  const addTime = async (minutes: number) => {
    setBusy(true);
    try {
      await apiPost(`/api/retrospectives/${publicId}/timer`, {
        addSeconds: minutes * 60,
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await apiPost(`/api/retrospectives/${publicId}/timer`, { action: "stop" });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const urgent = isRunning && remaining !== null && remaining <= 30_000;

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <span className="text-[0.7rem] font-bold uppercase tracking-[0.08em] text-ink-muted">
            {t("timer.label")}
          </span>
          <span
            role="timer"
            aria-live="off"
            style={
              urgent ? { animation: "timerBeat 1s ease-in-out infinite" } : undefined
            }
            className={`inline-block text-2xl font-bold tabular-nums ${
              urgent
                ? "text-red-500"
                : isRunning
                ? "text-nm-accent"
                : isFinished
                ? "text-ink-muted"
                : "text-ink-faint"
            }`}
          >
            {remaining === null
              ? "0:00"
              : isFinished
              ? t("timer.finished")
              : formatRemaining(remaining)}
          </span>
          {/* Announced rather than only shown: the end of a timebox matters to
              someone who is not watching the number. */}
          <span aria-live="polite" className="sr-only">
            {isFinished ? t("timer.finished") : ""}
          </span>
        </div>

        {canControl && (
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((minutes) => (
              <button
                key={minutes}
                onClick={() => addTime(minutes)}
                disabled={busy}
                className="chip chip-idle hover:bg-nm-accent/10 hover:text-nm-accent disabled:opacity-50"
              >
                {t("timer.addMinutes", { count: minutes })}
              </button>
            ))}
            {(isRunning || isFinished) && (
              <button
                onClick={stop}
                disabled={busy}
                className="chip bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50"
              >
                {t("timer.stop")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
